// src/utils/temperature.js
// =====================  WHERE THIS GOES  =====================
// New file. Pure temperature/thermal/airflow math — NO React, NO THREE
// (mirrors the role of wind.js / solar.js). temperatureScene.js and
// TemperaturePanel.jsx import from here. Sun geometry + time stepping +
// colour scale are REUSED from solar.js; this file only adds the
// heat-domain layers that solar.js does not have.
//
// EVERYTHING HERE IS DESIGN-STAGE SCREENING, not a load calc. It exists to
// size windows (passive), pick an HVAC family + rough tonnage (active), and
// shortlist facade/interior materials — enough to brief the architect and
// the MEP consultant, not to replace a detailed Manual-J / HAP / TRACE run.
//
// Coefficients that are code/standard values (verified, not from memory):
//   • ECBC/NBC 2016 climate zones — mean-monthly-max-temp + mean-monthly RH.
//   • IMAC (NBC 2016) natural-ventilation comfort: Tn = 0.54*Tout + 12.83,
//     ±2.38 K (90%) / ±3.46 K (80%), valid Tout 12.5–31 °C.
//   • NBC 2016: openable area for light+ventilation >= 10% of floor area.
// Material ρ/c/k are standard SP41 / ASHRAE textbook defaults — EDITABLE.
// ============================================================

export const DIM = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // days in month
export const HOURS_PER_DAY = 24;

// ---------------------------------------------------------------------------
// 1) IRRADIANCE DECOMPOSITION  (per timestep, per surface)
// ---------------------------------------------------------------------------
// Facades need DNI·cos(incidence) + DHI·SVF + ground-reflected; roof ≈ GHI.
// All inputs in W/m² (instantaneous); integrate over the day externally.
//   cosInc  = max(0, n · sunDir)      (0 when sun is behind the surface)
//   svf     = sky-view factor         (~0.5 vertical facade, ~1 roof)
//   cosTilt = cos(surface tilt)       (0 vertical, 1 horizontal/roof)
//   albedo  = ground reflectance      (~0.2 typical)
export const DEFAULT_ALBEDO = 0.2;
export const SVF_VERTICAL = 0.5;
export const SVF_ROOF = 1.0;

export function incidentWm2({ dni = 0, dhi = 0, ghi = 0 }, cosInc, svf, cosTilt, albedo = DEFAULT_ALBEDO) {
  const direct = dni * Math.max(0, cosInc);
  const diffuse = dhi * svf;
  const ground = albedo * ghi * (1 - cosTilt) / 2;
  return direct + diffuse + ground;
}

// Distribute a monthly-mean DAILY total (kWh/m²/day, as POWER returns) across
// sun-up timesteps weighted by sin(altitude), conserving the daily total.
// Returns an instantaneous W/m² for the given step. `wSumHours` = Σ sinAlt·Δt
// over the representative day (hours). NOTE: crude single diurnal shape for all
// three components — a real transposition (Perez, hourly) is the upgrade path.
export function dailyToInstantWm2(dailyKWh, sinAlt, wSumHours) {
  if (!(dailyKWh > 0) || !(wSumHours > 0) || sinAlt <= 0) return 0;
  // dailyKWh * 1000 = Wh/m²/day. inst(t)[W] = Wh_day * sinAlt / wSumHours.
  return (dailyKWh * 1000) * sinAlt / wSumHours;
}

// Simple analytic clear-sky GHI from solar altitude — the manual / offline
// fallback. Ignores clouds and OVERSTATES gain; label it as such in the UI.
export function clearSkyGHI(sinAlt, clearness = 1.0) {
  if (sinAlt <= 0) return 0;
  // 1090 W/m² * sinAlt * clearness, mild air-mass roll-off near the horizon.
  return 1090 * sinAlt * clearness * Math.pow(0.7, Math.pow(1 / Math.max(sinAlt, 0.05), 0.15));
}
// Split a clear-sky GHI into DNI/DHI (crude constant diffuse fraction).
export function clearSkySplit(ghi, sinAlt) {
  const dhi = 0.13 * ghi;
  const dni = sinAlt > 0.02 ? (ghi - dhi) / sinAlt : 0;
  return { ghi, dni: Math.max(0, dni), dhi: Math.max(0, dhi) };
}

// ---------------------------------------------------------------------------
// 2) MATERIALS  — ρ (kg/m³), c (J/kg·K), k (W/m·K). EDITABLE defaults.
// ---------------------------------------------------------------------------
export const MATERIALS = {
  brick:        { label: 'Burnt-clay brick', rho: 1920, c: 840,  k: 0.81 },
  rcc:          { label: 'RCC / concrete',   rho: 2400, c: 880,  k: 1.58 },
  aac:          { label: 'AAC block',        rho: 550,  c: 1000, k: 0.16 },
  flyash:       { label: 'Fly-ash block',    rho: 1000, c: 900,  k: 0.29 },
  stone:        { label: 'Stone (dense)',    rho: 2700, c: 790,  k: 2.50 },
  cementPlaster:{ label: 'Cement plaster',   rho: 1860, c: 840,  k: 0.72 },
  gypsum:       { label: 'Gypsum plaster',   rho: 1200, c: 840,  k: 0.50 },
  xps:          { label: 'XPS insulation',   rho: 35,   c: 1400, k: 0.034 },
  eps:          { label: 'EPS insulation',   rho: 24,   c: 1400, k: 0.036 },
  timber:       { label: 'Timber',           rho: 600,  c: 1200, k: 0.14 },
};
// surface + air resistances (m²K/W)
export const R_SI_WALL = 0.13, R_SO_WALL = 0.04;
export const R_SI_ROOF = 0.10, R_SO_ROOF = 0.04;
export const R_AIRGAP = 0.18;          // unventilated cavity, per gap
const PERIOD_S = 86400;                 // 24 h diurnal period

// layer = { mat: key, t: metres }  |  { airgap: true }
export function assemblyUValue(layers, kind = 'wall') {
  const rsi = kind === 'roof' ? R_SI_ROOF : R_SI_WALL;
  const rso = kind === 'roof' ? R_SO_ROOF : R_SO_WALL;
  let R = rsi + rso;
  layers.forEach((L) => {
    if (L.airgap) { R += R_AIRGAP; return; }
    const m = MATERIALS[L.mat]; if (!m || !(L.t > 0)) return;
    R += L.t / m.k;
  });
  return { U: 1 / R, R };
}
// areal heat capacity κ (kJ/m²K) — the honest "thermal mass" number.
export function arealHeatCapacity(layers) {
  let kappa = 0;
  layers.forEach((L) => {
    if (L.airgap) return;
    const m = MATERIALS[L.mat]; if (!m || !(L.t > 0)) return;
    kappa += m.rho * m.c * L.t;          // J/m²K
  });
  return kappa / 1000;                    // kJ/m²K
}
// APPROXIMATE periodic response (additive per-layer). Exact = admittance/
// transfer-matrix method (a clean later upgrade). Time lag in hours,
// decrement factor 0..1 (lower = more damped).
export function periodicResponse(layers) {
  let lag = 0, dec = 1;
  layers.forEach((L) => {
    if (L.airgap) return;
    const m = MATERIALS[L.mat]; if (!m || !(L.t > 0)) return;
    const alpha = m.k / (m.rho * m.c);                 // thermal diffusivity
    const d = Math.sqrt(alpha * PERIOD_S / Math.PI);   // penetration depth (m)
    lag += (L.t / 2) * Math.sqrt(PERIOD_S / (Math.PI * alpha)) / 3600;
    dec *= Math.exp(-L.t / d);
  });
  return { timeLagH: lag, decrement: Math.min(1, dec) };
}
export function massClass(kappa) {
  if (kappa < 60) return 'light';
  if (kappa < 165) return 'medium';
  return 'heavy';
}

// ---------------------------------------------------------------------------
// 3) SOLAR HEAT GAIN  (opaque via sol-air, glazing via SHGC)
// ---------------------------------------------------------------------------
export const H_O = 22;  // outside surface film coefficient (W/m²K), moderate wind
// Sol-air temperature for an opaque surface. absorptance α, incident W/m².
// Long-wave sky term omitted for walls (small); ~3.9 K for horizontal roofs.
export function solAirTemp(tOut, absorptance, incidentWm2Val, kind = 'wall') {
  const lw = kind === 'roof' ? 3.9 : 0;
  return tOut + (absorptance * incidentWm2Val) / H_O - lw;
}
/**
 * Sol-air SURFACE temperature per facade + roof, and the rise above ambient.
 *
 *   ΔT = (α · I) / h_o          walls
 *   ΔT = (α · I) / h_o − 3.9    roof (long-wave loss to a clear sky)
 *
 * `I` is the mean DAYTIME incident irradiance the Sun/Temperature sweep already
 * accumulates per facade, so this is a daytime-average rise — NOT an
 * instantaneous peak, which would be materially higher around solar noon on the
 * facade facing the sun.
 *
 * Screening-grade, and the limits matter:
 *  - h_o is fixed at H_O (moderate wind). Real film coefficient swings roughly
 *    12-35 W/m²K with wind speed, and ΔT scales inversely with it — a sheltered
 *    facade runs hotter than this reports, an exposed one cooler.
 *  - assumes an opaque surface at steady state; thermal mass delays and damps
 *    the real peak (see timeLagDecrement).
 *  - tOut must be a real air temperature; pass null rather than a guess and the
 *    rise is still reported while the absolute temperature stays null.
 *
 * @param wm2ByCard {N,E,S,W} mean daytime incident W/m²
 * @param roofWm2   mean daytime incident W/m² on the roof
 * @param absorptance solar absorptance α of the opaque surface (0..1)
 * @param tOut      ambient air temperature °C, or null if unknown
 */
export function surfaceTemperatures(wm2ByCard = {}, roofWm2 = 0, absorptance = 0.6, tOut = null) {
  const a = Math.max(0, Math.min(1, absorptance));
  const out = {};
  ['N', 'E', 'S', 'W'].forEach((c) => {
    const I = wm2ByCard[c] || 0;
    const rise = (a * I) / H_O;
    out[c] = {
      incidentWm2: Math.round(I),
      riseK: +rise.toFixed(1),
      surfaceC: tOut == null ? null : +solAirTemp(tOut, a, I, 'wall').toFixed(1),
    };
  });
  const riseR = (a * roofWm2) / H_O - 3.9;
  out.R = {
    incidentWm2: Math.round(roofWm2),
    riseK: +riseR.toFixed(1),
    surfaceC: tOut == null ? null : +solAirTemp(tOut, a, roofWm2, 'roof').toFixed(1),
  };
  const walls = ['N', 'E', 'S', 'W'];
  out.hottest = walls.reduce((x, y) => (out[y].riseK > out[x].riseK ? y : x), 'N');
  out.ambientC = tOut;
  out.absorptance = a;
  out.basis = 'daytime-average sol-air, h_o = ' + H_O + ' W/m²K';
  return out;
}

// Monthly opaque conduction gain per m² (kWh/m²/month) driven by sol-air.
// meanIncidentWm2 = mean daytime incident on the surface for the month.
export function opaqueGainKWhm2(U, tSolAirMean, tIn, daytimeHoursMonth) {
  const dT = tSolAirMean - tIn;               // + into building (gain), − loss
  return U * dT * daytimeHoursMonth / 1000;   // Wh→kWh
}
// Glazing solar gain per m² of glass (kWh/m²/month) = incident × SHGC.
export function glazingGainKWhm2(incidentKWhm2Month, shgc) {
  return incidentKWhm2Month * shgc;
}

// ---------------------------------------------------------------------------
// 4) CLIMATE ZONE  (ECBC / NBC 2016) from 12 months of {tMax, rh}
// ---------------------------------------------------------------------------
function monthZone(tMax, rh) {
  if (tMax == null || rh == null) return null;
  if (tMax > 30 && rh < 55) return 'hot-dry';
  if (tMax > 30 && rh >= 55) return 'warm-humid';
  if (tMax >= 25 && tMax <= 30 && rh < 75) return 'temperate';
  if (tMax < 25) return 'cold';
  return 'other';               // e.g. 25–30 & very humid → leans warm-humid
}
export function ecbcZone(perMonth) {
  const counts = {};
  perMonth.forEach((m) => {
    const z = monthZone(m.tMax, m.rh);
    if (z && z !== 'other') counts[z] = (counts[z] || 0) + 1;
  });
  let best = null, n = 0;
  Object.entries(counts).forEach(([z, c]) => { if (c > n) { best = z; n = c; } });
  const zone = n >= 6 ? best : 'composite';      // no category dominates ½ year
  return { zone, counts, monthly: perMonth.map((m) => monthZone(m.tMax, m.rh)) };
}
// Zone-specific design guidance for the Materials + HVAC cards.
export const ZONE_INFO = {
  'hot-dry':    { label: 'Hot-Dry',    targetU: 0.4, wantMass: 'heavy',  targetSHGC: 0.25,
                  evaporative: true,  note: 'High thermal mass + shading; night flush; evaporative cooling viable.' },
  'warm-humid': { label: 'Warm-Humid', targetU: 0.6, wantMass: 'light',  targetSHGC: 0.25,
                  evaporative: false, note: 'Light, shaded, cross-ventilated; latent-dominated → dehumidification.' },
  'composite':  { label: 'Composite',  targetU: 0.4, wantMass: 'medium', targetSHGC: 0.25,
                  evaporative: true,  note: 'Seasonal mix; moderate mass, shading in summer, gains in winter.' },
  'temperate':  { label: 'Temperate',  targetU: 0.5, wantMass: 'medium', targetSHGC: 0.35,
                  evaporative: false, note: 'Mild; modest mass, balanced glazing; low mechanical demand.' },
  'cold':       { label: 'Cold',       targetU: 0.3, wantMass: 'heavy',  targetSHGC: 0.5,
                  evaporative: false, note: 'Insulate heavily, capture solar gain, minimise infiltration loss.' },
};

// ---------------------------------------------------------------------------
// 5) ADAPTIVE COMFORT  (IMAC, NBC 2016 — naturally ventilated)
// ---------------------------------------------------------------------------
export const IMAC_BANDS = { 80: 3.46, 90: 2.38 };
export function imacComfort(toutRunningMean, level = 80) {
  const t = Math.max(12.5, Math.min(31, toutRunningMean));   // model validity
  const neutral = 0.54 * t + 12.83;
  const band = IMAC_BANDS[level] ?? IMAC_BANDS[80];
  return { neutral, low: neutral - band, high: neutral + band, band, clamped: t !== toutRunningMean };
}
// Classify a month: free-running indoor ≈ Tout(+ solar uplift). Returns the
// mechanical need if any. indoorEst defaults to the month's mean outdoor.
export function comfortVerdict(toutMean, indoorEst = null, level = 80) {
  const c = imacComfort(toutMean, level);
  const tin = indoorEst == null ? toutMean : indoorEst;
  let need = 'comfort';
  if (tin > c.high) need = 'cooling';
  else if (tin < c.low) need = 'heating';
  return { ...c, indoorEst: tin, need };
}

// ---------------------------------------------------------------------------
// 6) PASSIVE VENTILATION  — required openable area for a target ACH
// ---------------------------------------------------------------------------
export const G = 9.81;
export const CD_WIND = 0.35;   // cross-vent effectiveness (perpendicular ~0.35)
export const CD_STACK = 0.65;  // discharge coeff for stack openings
// Wind-driven volumetric flow per unit openable area (m³/s per m²) = Cv·V.
export function windFlowPerArea(windSpeed, cv = CD_WIND) { return cv * Math.max(0, windSpeed); }
// Stack flow per unit area = Cd·sqrt(2·g·ΔH·ΔT / Tavg_K).  ΔH = inlet↔outlet
// height separation (m), ΔT = |indoor−outdoor| (K), Tavg in K.
export function stackFlowPerArea(dHeight, dTemp, tAvgC = 28, cd = CD_STACK) {
  const tK = tAvgC + 273.15;
  const val = 2 * G * Math.max(0, dHeight) * Math.max(0, dTemp) / tK;
  return cd * Math.sqrt(Math.max(0, val));
}
// Combined effective flow per unit area (quadrature — standard combination).
export function combinedFlowPerArea(windSpeed, dHeight, dTemp, opts = {}) {
  const w = windFlowPerArea(windSpeed, opts.cv);
  const s = stackFlowPerArea(dHeight, dTemp, opts.tAvgC, opts.cd);
  return { wind: w, stack: s, total: Math.hypot(w, s) };
}
// Solve required openable area (m²) for a target air-change rate.
//   volume (m³), ach (1/h), flowPerArea (m³/s per m²).
// Returns physics area, NBC code floor (10% floor area), and the governing one.
export function openableAreaForACH({ volume, ach, floorAreaM2, windSpeed, dHeight, dTemp, opts = {} }) {
  const qTarget = ach * volume / 3600;                 // m³/s
  const f = combinedFlowPerArea(windSpeed, dHeight, dTemp, opts);
  const physicsArea = f.total > 1e-6 ? qTarget / f.total : Infinity;
  const codeFloor = 0.10 * floorAreaM2;                // NBC 2016 min opening
  const govern = Math.max(isFinite(physicsArea) ? physicsArea : 0, codeFloor);
  return {
    qTarget, physicsArea, codeFloor, governing: govern,
    windShare: f.total > 0 ? (f.wind ** 2) / (f.total ** 2) : 0,   // energy split
    stackShare: f.total > 0 ? (f.stack ** 2) / (f.total ** 2) : 0,
    flow: f,
    pctOfFloor: floorAreaM2 > 0 ? (govern / floorAreaM2) * 100 : 0,
  };
}

// ---------------------------------------------------------------------------
// 7) HVAC  — peak sizing (design day) + type recommendation
// ---------------------------------------------------------------------------
export const RHO_AIR = 1.2, CP_AIR = 1005, H_FG = 2450e3; // kg/m³, J/kgK, J/kg
export const TR_KW = 3.517;               // 1 ton of refrigeration = 3.517 kW
// Humidity ratio (kg/kg) from T(°C) and RH(%), rough psychrometrics.
export function humidityRatio(tC, rhPct) {
  const pws = 0.61078 * Math.exp((17.27 * tC) / (tC + 237.3)); // kPa (Tetens)
  const pw = (Math.max(0, Math.min(100, rhPct)) / 100) * pws;
  return 0.622 * pw / Math.max(1e-3, 101.325 - pw);
}
// Peak cooling load (kW) on a design day. Screening: envelope + solar + internal
// + ventilation. Returns sensible, latent, total, TR, and latent fraction.
export function peakCoolingLoad({
  envUA,            // Σ U·A over opaque envelope (W/K)
  glazingArea, shgc, peakGlazingWm2,   // direct solar through glass
  opaqueSolarW,     // extra opaque solar gain at peak (W) — from sol-air, optional
  tOutDesign, tIn = 25,
  internalW = 0,    // occupancy + lighting + equipment (W)
  ventM3s, rhOut = 60, rhIn = 55,
}) {
  const envelope = Math.max(0, envUA * (tOutDesign - tIn));
  const glass = Math.max(0, (glazingArea || 0) * (peakGlazingWm2 || 0) * (shgc || 0));
  const ventSens = Math.max(0, (ventM3s || 0) * RHO_AIR * CP_AIR * (tOutDesign - tIn));
  const wOut = humidityRatio(tOutDesign, rhOut), wIn = humidityRatio(tIn, rhIn);
  const ventLat = Math.max(0, (ventM3s || 0) * RHO_AIR * H_FG * (wOut - wIn));
  const sensible = envelope + glass + (opaqueSolarW || 0) + internalW + ventSens;
  const latent = ventLat;
  const total = sensible + latent;
  return {
    sensibleKW: sensible / 1000, latentKW: latent / 1000, totalKW: total / 1000,
    tr: total / 1000 / TR_KW, latentFraction: total > 0 ? latent / total : 0,
    parts: { envelopeKW: envelope / 1000, glassKW: glass / 1000, ventSensKW: ventSens / 1000, ventLatKW: ventLat / 1000, internalKW: internalW / 1000 },
  };
}
// Recommend an HVAC family from load, latent share, zone, and size.
export function hvacTypeRecommendation({ tr, latentFraction, zone, floorAreaM2 }) {
  const zi = ZONE_INFO[zone] || {};
  const humid = latentFraction > 0.30;
  const big = tr > 15 || floorAreaM2 > 400;
  const options = [];
  if (zi.evaporative && !humid) {
    options.push({ type: 'Evaporative / desert cooling', why: 'dry climate, low latent — cheap, low-energy sensible cooling.' });
  }
  if (humid) {
    options.push({ type: big ? 'VRF/VRV + DOAS (dehumidified fresh air)' : 'Inverter split / DX with dehumidification',
      why: 'latent-dominated — needs moisture removal an evaporative system cannot provide.' });
  } else {
    options.push({ type: big ? 'VRF/VRV (zoned)' : 'Inverter split AC',
      why: big ? 'multiple zones, part-load efficiency at this size.' : 'small sensible load, simplest fit.' });
  }
  if (big) options.push({ type: 'Chilled-water (central plant)', why: 'large connected load may justify central plant + AHUs.' });
  return { primary: options[0], options, humid, big, zoneNote: zi.note };
}

// ---------------------------------------------------------------------------
// 8) ROOFTOP PV  — feasibility screening (twin of Wind's rooftop energy)
// ---------------------------------------------------------------------------
export const PV_DEFAULTS = { moduleEff: 0.19, performanceRatio: 0.75, coverage: 0.7 };
// roofGHIByMonth: [12] kWh/m²/month incident on the roof (obstruction-aware).
export function pvYield(roofGHIByMonth, roofAreaM2, opts = {}) {
  const eff = opts.moduleEff ?? PV_DEFAULTS.moduleEff;
  const pr = opts.performanceRatio ?? PV_DEFAULTS.performanceRatio;
  const cov = opts.coverage ?? PV_DEFAULTS.coverage;       // usable roof fraction
  const arrayArea = roofAreaM2 * cov;
  const monthly = roofGHIByMonth.map((g) => (g || 0) * arrayArea * eff * pr);
  const annual = monthly.reduce((s, v) => s + v, 0);
  return { monthly, annualKWh: annual, arrayArea, capKWp: arrayArea * eff }; // ~1kW/m² STC
}

// ---------------------------------------------------------------------------
// colour scale — reuse solar.heatCss in the panel/scene; hottest→coolest.
// (kept here as a convenience re-export point if callers prefer temperature.js)
// ---------------------------------------------------------------------------
export { heatCss, heatColor } from './solar';

// facade compass bucket label
export const FACE_NAME = { N: 'North', E: 'East', S: 'South', W: 'West' };
